/**
 * Twilio (super admin) — W6, B3-TwilioConfig, DUP-5, QW-h11.
 *
 * Was one 901-line page mixing the account, the cross-tenant numbers, the
 * costs and a fix procedure. Now:
 *   - a health check on top, whatever the tab: each line says what is wrong,
 *     why (missing variable, refused account, wrong voice URL) and what to do,
 *     from GET /api/admin/platform/twilio-health (read only). The old
 *     TwilioDiagnostic screen redirects here (DUP-5);
 *   - three tabs in `?tab=`: Opérateurs (account, balance, TwiML App,
 *     credentials present or not), Numéros (server-paged DataTable, SR1-1.2),
 *     Coûts (month in the active language, QW-h11);
 *   - no hosting console link (the DigitalOcean link leaked the infrastructure).
 * W7 (SU-4): « SIP et Zadarma » under the Opérateurs tab (SIP domain, import
 * of a Zadarma number).
 * Nothing here changes the TwiML App or the TwiML routes.
 */
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { getTwilioHealth } from '@/api/endpoints/platform';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useTabParam } from '@/hooks/useTabParam';
import { PlatformPageHeader } from '@/components/backoffice/platform/PlatformBits';
import { TwilioHealthPanel } from '@/components/backoffice/twilio/TwilioHealthPanel';
import { TwilioOperatorsTab } from '@/components/backoffice/twilio/TwilioOperatorsTab';
import { TwilioNumbersTab } from '@/components/backoffice/twilio/TwilioNumbersTab';
import { TwilioCostsTab } from '@/components/backoffice/twilio/TwilioCostsTab';
import { TwilioSipZadarmaPanel } from '@/components/backoffice/twilio/TwilioSipZadarmaPanel';

export const TWILIO_TABS = ['operators', 'numbers', 'costs'] as const;
type TwilioTab = (typeof TWILIO_TABS)[number];

export default function TwilioConfig() {
  const { t } = useTranslation(['admin']);
  const [tab, setTab] = useTabParam<TwilioTab>(TWILIO_TABS, 'operators');
  const health = useQuery({ queryKey: ['twilio-health'], queryFn: ({ signal }) => getTwilioHealth(signal), retry: false });
  const h = health.data;
  // Costs wait for the health check: no Twilio call when the account cannot
  // answer. An unreadable health check lets the costs try on their own.
  const twilioConfigured = health.isError ? true : h ? h.credentials.TWILIO_ACCOUNT_SID && h.credentials.TWILIO_AUTH_TOKEN && (!h.account.checked || h.account.reachable) : null;

  return (
    <div className="mx-auto max-w-7xl space-y-5 p-4 sm:p-6">
      <PlatformPageHeader title={t('admin:superAdminLayout.nav.twilio')} subtitle={t('admin:twilioConfig.subtitle')} />
      <TwilioHealthPanel
        health={h}
        isLoading={health.isLoading}
        isFetching={health.isFetching}
        isError={health.isError}
        error={health.error}
        onRecheck={() => void health.refetch()}
      />
      <Tabs value={tab} onValueChange={(v) => setTab(v as TwilioTab)}>
        <TabsList>
          {TWILIO_TABS.map((id) => <TabsTrigger key={id} value={id}>{t(`admin:twilioConfig.tabs.${id}`)}</TabsTrigger>)}
        </TabsList>
        <TabsContent value="operators" className="pt-4">
          <TwilioOperatorsTab health={h} isError={health.isError} />
          <div className="pt-5"><TwilioSipZadarmaPanel /></div>
        </TabsContent>
        <TabsContent value="numbers" className="pt-4">
          {tab === 'numbers' && <TwilioNumbersTab />}
        </TabsContent>
        <TabsContent value="costs" className="pt-4">
          {tab === 'costs' && <TwilioCostsTab twilioConfigured={twilioConfigured} />}
        </TabsContent>
      </Tabs>
    </div>
  );
}
